// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the small drawn miniatures on the Customise Home layout and density cards. They are
// plain spans (no screenshots) so they follow the theme and stay crisp at any size.

import styles from '@app/features/konvo_today/components/settings/CustomiseHome.module.css';
import type {
	HomeAllChatsGrouping,
	HomeDensity,
	HomeLayout,
	HomeOpenConversations,
} from '@app/features/konvo_today/state/HomePreferences';
import {clsx} from 'clsx';
import type React from 'react';

/** Avatar hues for the little faces; any order reads as "different people". */
const HUES = [100, 340, 20, 280, 200, 150, 180, 10];

const hue = (h: number) => ({'--mp-hue': h}) as React.CSSProperties;

function Row({h, tick = true, avatar = true}: {h: number; tick?: boolean; avatar?: boolean}) {
	return (
		<span className={styles.mpRow}>
			{tick ? <i className={styles.mpTick} /> : null}
			{avatar ? <i className={styles.mpFace} style={hue(h)} /> : null}
			<u className={styles.mpName} />
			<u className={styles.mpLine} />
		</span>
	);
}

function Heading() {
	return (
		<>
			<i className={styles.mpKicker} />
			<i className={styles.mpTitle} />
		</>
	);
}

function StacksPreview() {
	return (
		<span className={styles.mp}>
			<Heading />
			<span className={styles.mpCard}>
				{HUES.slice(0, 3).map((h) => (
					<Row key={h} h={h} />
				))}
				<span className={styles.mpRow}>
					<span className={styles.mpFaces}>
						{[280, 200, 150].map((h) => (
							<i key={h} className={styles.mpFace} style={hue(h)} />
						))}
					</span>
					<u className={clsx(styles.mpLine, styles.mpLineShort)} />
				</span>
			</span>
			<span className={styles.mpCard}>
				<span className={styles.mpRow}>
					<i className={clsx(styles.mpFace, styles.mpFaceSquare)} style={hue(250)} />
					<u className={styles.mpLine} />
					<u className={styles.mpInk} />
				</span>
			</span>
			<i className={styles.mpGhost} />
			<i className={clsx(styles.mpGhost, styles.mpGhost2)} />
			<span className={styles.mpCard}>
				<Row h={340} tick={false} />
			</span>
			<i className={styles.mpGhost} />
		</span>
	);
}

function ListPreview() {
	return (
		<span className={styles.mp}>
			<Heading />
			<span className={styles.mpCard}>
				{HUES.map((h) => (
					<Row key={h} h={h} />
				))}
			</span>
			<span className={styles.mpCard}>
				{[340, 200].map((h) => (
					<Row key={h} h={h} tick={false} />
				))}
			</span>
		</span>
	);
}

function DeckPreview() {
	return (
		<span className={styles.mp}>
			<Heading />
			<span className={styles.mpProgress}>
				{[0, 1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
					<i key={index} className={clsx(styles.mpSegment, index < 2 && styles.mpSegmentOn)} />
				))}
			</span>
			<span className={clsx(styles.mpCard, styles.mpDeckCard)}>
				<span className={styles.mpRow}>
					<i className={clsx(styles.mpFace, styles.mpFaceBig)} style={hue(340)} />
					<u className={clsx(styles.mpLine, styles.mpLineShort, styles.mpLineFlush)} />
				</span>
				<u className={styles.mpHeadline} />
				<u className={clsx(styles.mpHeadline, styles.mpHeadlineShort)} />
				<i className={styles.mpBubble} />
				<span className={styles.mpRow}>
					<i className={styles.mpRound} />
					<u className={styles.mpButton} />
					<i className={styles.mpRound} />
				</span>
			</span>
			<i className={styles.mpGhost} />
		</span>
	);
}

function TimelinePreview() {
	return (
		<span className={styles.mp}>
			<Heading />
			<span className={clsx(styles.mpCard, styles.mpTimeline)}>
				<span className={styles.mpNow}>
					<u className={styles.mpNowLine} />
				</span>
				{[0, 1].map((index) => (
					<span key={index} className={styles.mpTlRow}>
						<i className={styles.mpTlDot} />
						<i className={styles.mpTick} />
						<u className={styles.mpLine} />
					</span>
				))}
				<span className={styles.mpTlRow}>
					<i className={clsx(styles.mpTlDot, styles.mpTlDotMeeting)} />
					<i className={styles.mpBlock} />
				</span>
				{[2, 3].map((index) => (
					<span key={index} className={styles.mpTlRow}>
						<i className={styles.mpTlDot} />
						<i className={styles.mpTick} />
						<u className={styles.mpLine} />
					</span>
				))}
				<span className={styles.mpTlRow}>
					<i className={clsx(styles.mpTlDot, styles.mpTlDotMeeting)} />
					<i className={styles.mpBlock} />
				</span>
			</span>
		</span>
	);
}

/** Opening a conversation: a card over Home, or Home as a list with the conversation beside it. */
export function HomeOpenPreview({value}: {value: HomeOpenConversations}) {
	const chat = (
		<>
			<span className={styles.mpRow}>
				<i className={styles.mpFace} style={hue(340)} />
				<u className={styles.mpName} />
			</span>
			<i className={styles.mpChat} />
			<i className={clsx(styles.mpChat, styles.mpChatMine)} />
			<u className={styles.mpBox} />
		</>
	);
	if (value === 'popup') {
		return (
			<span className={clsx(styles.mp, styles.mpOpen)}>
				<Heading />
				<span className={clsx(styles.mpCard, styles.mpUnder)}>
					{HUES.slice(0, 5).map((h) => (
						<Row key={h} h={h} tick={false} />
					))}
				</span>
				<span className={styles.mpPopup}>{chat}</span>
			</span>
		);
	}
	return (
		<span className={styles.mpSplit}>
			<span className={clsx(styles.mp, styles.mpSplitList)}>
				<i className={clsx(styles.mpTitle, styles.mpTitleShort)} />
				<span className={styles.mpCard}>
					{HUES.slice(0, 6).map((h) => (
						<Row key={h} h={h} tick={false} />
					))}
				</span>
			</span>
			<span className={styles.mpSplitPane}>{chat}</span>
			<i className={styles.mpRailStub} />
		</span>
	);
}

export const HOME_LAYOUT_PREVIEW: Record<HomeLayout, React.ComponentType> = {
	stacks: StacksPreview,
	list: ListPreview,
	deck: DeckPreview,
	timeline: TimelinePreview,
};

export function HomeDensityPreview({density}: {density: HomeDensity}) {
	const compact = density === 'compact';
	const hues = compact ? HUES.slice(0, 6) : HUES.slice(0, 3);
	return (
		<span className={clsx(styles.mp, styles.mpDensity)}>
			<span className={clsx(styles.mpCard, compact ? styles.mpCardCompact : styles.mpCardComfortable)}>
				{hues.map((h) => (
					<Row key={h} h={h} avatar={!compact} />
				))}
			</span>
		</span>
	);
}

/** All chats: day headings over mixed rows, or a coloured connection mark over each group. */
export function HomeAllChatsPreview({value}: {value: HomeAllChatsGrouping}) {
	if (value === 'day') {
		return (
			<span className={styles.mp}>
				<i className={styles.mpKicker} />
				<span className={styles.mpCard}>
					{HUES.slice(0, 4).map((h) => (
						<Row key={h} h={h} tick={false} />
					))}
				</span>
				<i className={styles.mpKicker} />
				<span className={styles.mpCard}>
					{HUES.slice(4, 7).map((h) => (
						<Row key={h} h={h} tick={false} />
					))}
				</span>
			</span>
		);
	}
	// Konvo violet, WhatsApp green, Outlook blue.
	return (
		<span className={styles.mp}>
			{[265, 140, 210].map((h, group) => (
				<span key={h} className={styles.mpGroup}>
					<span className={styles.mpRow}>
						<i className={styles.mpGroupMark} style={hue(h)} />
						<i className={clsx(styles.mpKicker, styles.mpGroupName)} />
					</span>
					<span className={styles.mpCard}>
						{HUES.slice(group * 2, group * 2 + 2).map((face) => (
							<Row key={face} h={face} tick={false} />
						))}
					</span>
				</span>
			))}
		</span>
	);
}
